import { RxExtensions } from 'app/core/components/common/rx.component';
import { AddImageComponent } from './add-image/add-image.component';
import { SmartModalService } from 'app/core/services/smart-modal.service';
import { PlaceholderBlot, ImageBlot, Url } from './placeholder-ext';
import { RxComponent } from 'app/core/components/common/rx.component';
import { ReplaySubject, Subject, Observable, BehaviorSubject } from 'rxjs/';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, OnInit, ViewChild, ViewEncapsulation, AfterViewInit, EventEmitter, Output, forwardRef, Input, SimpleChanges } from '@angular/core';

declare var Quill: any;
export const GlobalQuill = {
    instance: null,
    onAdded: new Subject<any>(),
    onDeleted: new Subject<any>()
 };

export class GlobalQuillProvider {

    private static registry = {};

    static get(id: number): any {
        return this.registry[id];
    }

    static register(id: number, instance: any) {
        if (this.registry[id]) {
            throw new Error(`Quill with id ${id} already registered`);
        }

        if (!instance) {
            throw new Error('Provided quill instance cannot be null');
        }

        return this.registry[id] = instance
    }

    static unregister(id: number) {
        if (!this.registry[id]) {
            throw new Error(`Quill with id ${id} missing. Already unregistered?`);
        }

        delete this.registry[id];
    }
 }

export const EDITOR_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EditorCommonComponent),
    multi: true,
};

declare var $: any;
declare var SnowTooltip: any;

@Component({
    selector: 'itpx-editor-common',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.css'],
    encapsulation: ViewEncapsulation.None,
    providers: [EDITOR_VALUE_ACCESSOR]
})
export class EditorCommonComponent extends RxComponent implements ControlValueAccessor {

    private readonly EMPTY_EVENT = {};
    private static id = 0;

    private id: number;
    private editorId;
    private toolbarId;

    private changeFn = (val) => {};
    private touchFn = () => {};

    private quill;

    @ViewChild('container') containerElement;
    @ViewChild('editor') editorElement;
    private tooltipCoords: Observable<any>;

    @Input()
    private placeholders: any[];
    @Input()
    private title: string;
    @Input()
    private animate: boolean = false
    
    private placeholderAdd = new Subject<any>();
    private valueUpdate = new ReplaySubject<string>();
    private toggleDisable = new ReplaySubject<boolean>();

    @Output()
    private valueChanged = new EventEmitter<string>();
    private activated: Observable<boolean>;

    constructor(private modalService: SmartModalService) {
        super();
        this.id = EditorCommonComponent.id++;
        this.editorId = `editor-${this.id}`;
        this.toolbarId = `toolbar-${this.id}`;
    }

    ngAfterViewInit() {

        // Important! Simply referencing the classes loads them
        // TODO: Find a way to add them in webpack pipeline
        let blot = PlaceholderBlot;  
        let imageBlot = ImageBlot;  
        let urlBlot = Url;

        this.quill = new Quill('#' + this.editorId, {
            modules: {
                history: {},
                toolbar: { container: '#' + this.toolbarId }
            },
            placeholder: 'Enter a custom message',
            theme: 'snow'
        });

        GlobalQuillProvider.register(this.id, this.quill);

        this.toggleDisable
            .bindToLifecycle(this)
            .subscribe(disabled => disabled ? this.quill.disable() : this.quill.enable());

        this.valueUpdate
            // Quill breaks if val is undefined (but works w/ null)
            .filter(val => val !== undefined)
            .bindToLifecycle(this)
            .subscribe(val => this.quill.pasteHTML(val));

        this.quill.on('text-change', (delta, old, src) => {
            this.changeFn(this.quill.root.innerHTML);
            this.valueChanged.emit(this.quill.root.innerHTML);
        });

        this.placeholderAdd
            .switchMap(placeholder => placeholder.type == 'image' ?
                this.modalService.open(AddImageComponent, placeholder.displayValue, 'Insert Image')
                    .neverError()
                    .map(result => Object.assign(placeholder, { dimensions: result.result })) :
                // Nothing special to do, just emit placeholder
                Observable.of(placeholder)
            )
            .bindToLifecycle(this)
            .subscribe(data => {
                switch (data.type) {
                    case 'url': this.addLink(data); break;
                    case 'image': this.addImage(data); break;
                    case 'text': 
                    default: this.addText(data); break;
                }

                this.quill.focus();                
            });

        this.tooltipCoords = RxExtensions.fromMutations(this.editorElement.nativeElement, { childList: true, subtree: true })
            .flatMap(mutations => Observable.from(mutations))
            .filter(mutation => this.containsAnchor(mutation.addedNodes) || this.containsAnchor(mutation.removedNodes))
            .startWith(this.EMPTY_EVENT)
            .debounceTime(50)
            .map(_ => document.querySelectorAll(`#${this.editorId} a.ql-url`))
            .switchMap(anchorElements => Observable.merge(
                    Observable.fromEvent(anchorElements, 'mouseover'),
                    Observable.fromEvent(anchorElements, 'mouseout'),
                )
                .startWith(this.EMPTY_EVENT)
            ) 
            .map(event => this.mapToTooltipCoords(event))
            .shareReplay();

        let editorElement = document.querySelector(`#${this.editorId} > .ql-editor`);
        let editorFocusChange = Observable.merge(
                Observable.fromEvent(editorElement, 'focusin').mapTo(true),
                Observable.fromEvent(editorElement, 'focusout').mapTo(false)
            )
            .startWith(false)
            .share();
        
        let containerElement = this.containerElement.nativeElement;
        let editorHoverChange = Observable.merge(
                Observable.fromEvent(containerElement, 'mouseover').mapTo(true),
                Observable.fromEvent(containerElement, 'mouseout').mapTo(false)
            )
            .startWith(false)
            .share();

        this.activated = Observable.combineLatest(
                editorHoverChange,
                // This makes sure rapid focus changes do not result in animation glitch
                editorFocusChange.debounceTime(10),
                (hovered, focused) => hovered || focused
            )
            .share();
        
        // Quill toolbar generates a span list from the select
        let self = this;
        $(`#${this.toolbarId} .ql-placeholder`).click(function() {
            $(this).find('.ql-picker-options').children()
                .each((_, child) => {
                    // Preserve text selection by retaining focus
                    self.quill.focus();

                    // Unselect any selected elements to allow user to click the same item multiple times in a row
                    $(child).removeClass('ql-selected');

                    // Add icons
                    let key = $(child).attr('data-value');
                    let placeholder = self.placeholders.find(p => p.key === key);
                    if (!placeholder) return;

                    let faIcon = 'fa-text-width';
                    switch (placeholder.type) {
                        case 'url': faIcon = 'fa-link'; break;
                        case 'image': faIcon = 'fa-picture'; break;
                    }

                    if ($(child).children().length === 0) $(child).append(`<i class="fa ${faIcon}""></i>`);
                });
        });
    }

    private mapToTooltipCoords(event) {
        let element = event.target;
        let type = event.type;       

        if (type == 'mouseover') {
            let offset = $(element).offset();
            let containerOffset = $(this.containerElement.nativeElement).offset();

            let message = null;
            let isPlaceholder = element.classList.contains('ql-placeholder-url');
            if (isPlaceholder) {
                let key = element.getAttribute('data-id');
                message = this.findPlaceholderByKey(key).displayValue;
            } else {
                message = element.getAttribute('href');
            }
            
            return {
                show: true, 
                top: (offset.top - containerOffset.top + 20) + 'px', 
                left: (offset.left - containerOffset.left) + 'px',
                message: message,
                isPlaceholder: isPlaceholder
            };
        } 

        return {};
    }

    private containsAnchor(nodes: NodeList): boolean {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes.item(i).nodeName == 'A') {
                return true;
            }
        }

        return false;
    }

    private get range(): { index: number, length: number } {
        return this.quill.selection.savedRange;
    }

    private onPlaceholderSelect(key: string) {
        this.addPlaceholder({key: key, value: null});
    }

    private findPlaceholderByKey(key: string) {
        return this.placeholders.find(p => p.key == key);
    }

    public addPlaceholder(item: {key: string, value: string, type?: string}) {
        let placeholder = this.findPlaceholderByKey(item.key);
        if (placeholder) {
            this.placeholderAdd.next(placeholder);
        }
    }

    private addLink(placeholder: any) {
        if (!this.range || this.range.length == 0) {
            // No selection to turn into hyperlink
            return;
        }

        this.quill.format('link', {key: placeholder.key, url: placeholder.previewValue});
    }

    private addText(placeholder: any) {
        const position = this.range && this.range.index || 0;
        
        this.quill.insertEmbed(position, 'holder', { id: placeholder.key, label: placeholder.displayValue }, Quill.sources.API);
        this.quill.insertText(position + 1, ' ', Quill.sources.API);
        this.quill.setSelection(position + 2, Quill.sources.API);
    }

    private addImage(placeholder: any) {
        this.quill.insertText(this.range.index, '\n', Quill.sources.USER);
        this.quill.insertEmbed(this.range.index + 1, 'image', {
            key: placeholder.key,
            width: placeholder.dimensions.width,
            height: placeholder.dimensions.height,
            alt: placeholder.displayValue,
            url: placeholder.previewValue,
        }, Quill.sources.USER);

        this.quill.setSelection(this.range.index + 2, Quill.sources.SILENT);
    }

    public undo(focus: boolean = true) {
        this.quill.history.undo();
        if (focus) this.quill.focus();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.quill.off('text-change');
        $('.ql-placeholder').off('click');
        GlobalQuillProvider.unregister(this.id);
    }

    writeValue(obj: any): void {
        this.valueUpdate.next(obj);
    }

    registerOnChange(fn: any): void {
        this.changeFn = fn;
    }

    registerOnTouched(fn: any): void {
    }

    setDisabledState(isDisabled: boolean): void {
        this.toggleDisable.next(isDisabled);
    }
}



// WEBPACK FOOTER //
// ./src/app/common/template/editor/editor.component.ts